import { isProduction } from "$lib/environment"
import type { FormDataAdapter } from "$lib/util"
import { ITIN, SSN } from "$lib/util/tax-information"
import type { ApplicationFormAccessor } from "$lib/yup"
import {
  Customer,
  MARITAL_STATUSES,
  MINIMUM_AGE,
  TAX_NUMBER_TYPES,
  ValidateYup,
  convertDateToISOString,
  parseISODateString,
  type CustomerSuitability,
} from "@tastyworks/tastyworks-api"
import {
  dateISOString,
  maxAge,
  minAge,
} from "@tastyworks/tastyworks-api/validate"
import { bool, number, object, string, type InferType } from "yup"

export function isValidTaxNumber(value: string | undefined): boolean {
  if (!value) {
    return true
  }

  if (!isProduction() && value.startsWith("666")) {
    return true
  }

  return SSN.test(value) || ITIN.test(value)
}

export const taxNumberSchema = object({
  isForeigner: bool().default(false),
  hasPendingOrApprovedApplication: bool(),
  hasProvidedTaxNumber: bool().default(false),
  taxNumber: string().when(
    ["hasPendingOrApprovedApplication", "isForeigner", "hasProvidedTaxNumber"],
    (
      [hasPendingOrApprovedApplication, isForeigner, hasProvidedTaxNumber],
      schema
    ) => {
      if (hasPendingOrApprovedApplication || hasProvidedTaxNumber) {
        return schema.nullable()
      }
      if (isForeigner) {
        // assume that 40 characters is less than the world's longest tax number
        return schema.max(40).required()
      }

      return schema
        .transform((value) => value?.replaceAll("-", ""))
        .length(9)
        .test(
          "invalid-tax-number",
          "yup.common.taxInformation.invalidTaxNumber",
          isValidTaxNumber
        )
        .required()
    }
  ),
  taxNumberType: string().nullable().oneOf(Object.values(TAX_NUMBER_TYPES)),
})

export const taxBirthDateSchema = object({
  birthDate: dateISOString()
    .required()
    .test(minAge(MINIMUM_AGE))
    .test(maxAge(120)),
})

export const taxFamilySchema = object({
  numberOfDependents: number().required(),
  maritalStatus: string().required().oneOf(Object.values(MARITAL_STATUSES)),
})

export const subjectToTaxWithholdingSchema = ValidateYup.customerSchema.pick([
  "subjectToTaxWithholding",
])

export const taxInfoSchema = object({
  taxNumber: taxNumberSchema,
  family: taxFamilySchema,
  birthDate: taxBirthDateSchema,
  subjectToTaxWithholding: subjectToTaxWithholdingSchema,
})

export const taxFamilyAdapter: FormDataAdapter<
  CustomerSuitability,
  InferType<typeof taxFamilySchema>
> = {
  toForm(customerSuitability, taxInformation) {
    taxInformation.maritalStatus =
      customerSuitability.maritalStatus ?? undefined
    taxInformation.numberOfDependents =
      customerSuitability.numberOfDependents ?? undefined

    return taxInformation
  },
  toModel(taxInformation, customerSuitability) {
    customerSuitability.maritalStatus = taxInformation.maritalStatus
    customerSuitability.numberOfDependents = taxInformation.numberOfDependents

    return customerSuitability
  },
}

export const taxNumberAdapter: FormDataAdapter<
  Customer,
  InferType<typeof taxNumberSchema>
> = {
  toForm(customer, taxInformation) {
    taxInformation.hasProvidedTaxNumber = !!customer.taxNumber
    taxInformation.taxNumber = ""
    taxInformation.taxNumberType = customer.taxNumberType

    return taxInformation
  },
  toModel(taxInformation, customer) {
    customer.taxNumber = taxInformation.taxNumber ?? ""
    customer.taxNumberType =
      taxInformation.taxNumberType ?? TAX_NUMBER_TYPES.SSN

    return customer
  },
}

export const taxBirthDateAdapter: FormDataAdapter<
  Customer,
  InferType<typeof taxBirthDateSchema>
> = {
  toForm(customer, taxInformation) {
    taxInformation.birthDate = convertDateToISOString(customer.birthDate)

    return taxInformation
  },
  toModel(taxInformation, customer) {
    customer.birthDate = parseISODateString(taxInformation.birthDate)

    return customer
  },
}

export const subjectToTaxWithholdingAdapter: FormDataAdapter<
  Customer,
  ApplicationFormAccessor
> = {
  toForm(customer, applicationFormAccessor) {
    applicationFormAccessor.subjectToTaxWithholding =
      customer.subjectToTaxWithholding ?? undefined
    return applicationFormAccessor
  },
  toModel(applicationFormAccessor, customer) {
    customer.subjectToTaxWithholding =
      applicationFormAccessor.subjectToTaxWithholding ||
      applicationFormAccessor.businessSubjectToTaxWithholding ||
      false
    return customer
  },
}
